/**
 * The schools front door (Aran, 2026-10-30, job #153). saysomethingin.app/schools
 * is the one link SSi gives schools, so a signed-out visitor to the bare
 * address is asked what they came to do before anything else: set up a new
 * school, set up a teacher account in their school's existing one, or sign in.
 * Before this the address was a sign-in form only, and a teacher from Abbey
 * Primary signed up through it into an account with no school.
 *
 * The chooser shows only on the bare /schools address. A deep link to a
 * schools page keeps going straight to the sign-in form, because whoever holds
 * that link already has an account. A sign-in someone was sent to on purpose
 * also skips it: a session that ran out, an admin hand-off carrying ?next=, or
 * a link carrying ?signin or ?step=signin. Choosing a card only adds ?step=,
 * so any other query string on the page survives into the step that follows.
 */
export interface FrontDoorInput {
  /** The route path, e.g. '/schools' or '/schools/teachers'. */
  path: string
  query: Record<string, unknown>
  /** The container's showLogin: signed out, no demo context, auth settled. */
  signedOut: boolean
  /** The calm "sign in again" notice after a revoked session. */
  sessionExpired: boolean
}

/**
 * ONE PANEL FOR EVERY CARD (Aran, 2026-10-30, job #182). Each card on the
 * front door opens in the same right-hand panel as sign-in, under the same red
 * hero, and each step is its own history entry on the bare /schools address:
 *
 *   /schools                  the cards
 *   /schools?step=signin      sign in            (?signin=1 is the older spelling)
 *   /schools?step=new-school  set up a new school   (the /schools1 flow)
 *   /schools?step=teacher     teacher invite code   (the /redeem flow; &code= deep-links one)
 *   /schools?step=tutor       independent tutor     (the /tutors flow)
 *
 * The three sign-up steps stay open once the visitor signs in partway through
 * (they all end signed in and move on by themselves), so they show whatever
 * the session. Sign-in and the cards are for a signed-out visitor only.
 */
export type DoorStep = 'cards' | 'signin' | FlowStep
export type FlowStep = 'new-school' | 'teacher' | 'tutor'
export const FLOW_STEPS: readonly FlowStep[] = ['new-school', 'teacher', 'tutor']

function isBareSchools(path: string): boolean {
  return path.replace(/\/+$/, '') === '/schools'
}

/** The sign-up flow a /schools address names, whatever the session; null for none. */
export function flowStepOf(path: string, query: Record<string, unknown>, tutorLaneOpen = true): FlowStep | null {
  if (!isBareSchools(path)) return null
  const step = query.step
  if (typeof step !== 'string' || !(FLOW_STEPS as readonly string[]).includes(step)) return null
  if (step === 'tutor' && !tutorLaneOpen) return null
  return step as FlowStep
}

/** Which panel the /schools front door shows, or null when it shows none of its own. */
export function doorStep(input: FrontDoorInput & { tutorLaneOpen?: boolean }): DoorStep | null {
  const { path, query, signedOut, sessionExpired } = input
  const flow = flowStepOf(path, query, input.tutorLaneOpen ?? true)
  if (flow) return flow
  if (!signedOut || sessionExpired) return null
  if (!isBareSchools(path)) return null
  if (query.next != null) return null
  if (query.step === 'signin' || query.signin != null) return 'signin'
  return 'cards'
}

export function showsFrontDoor(input: FrontDoorInput): boolean {
  return doorStep(input) === 'cards'
}

/**
 * Whether the sign-in form was reached from the cards, so a Back belongs
 * on it: the same page as the chooser, opened by ?step=signin (or the older
 * ?signin) and nothing else (a ?next hand-off or a session-expired notice has
 * no cards to go back to).
 */
export function canReturnToFrontDoor(input: FrontDoorInput): boolean {
  return doorStep(input) === 'signin'
}

/** The /schools address that opens a step; the cards when step is 'cards'. */
export function doorStepQuery<Q extends Record<string, unknown>>(query: Q, step: DoorStep): Q {
  const next: Record<string, unknown> = { ...query }
  delete next.signin
  delete next.step
  delete next.from
  if (step !== 'teacher') delete next.code
  if (step !== 'cards') next.step = step
  return next as Q
}
